import React, { FC, useState, memo } from 'react';
import Dropzone from 'react-dropzone';
import { toastr } from 'react-redux-toastr';
import { IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CloudUpload as CloudUploadIcon, Cancel as CancelIcon } from '@material-ui/icons';
import { ITheme } from 'src/app/App';
import { getImage } from 'src/app/redux/api';
import { TabsID } from '../common/interfaces';

const RAISE_SUPPORT_TICKET_URL = 'https://apps.powerapps.com/play/e/default-24fe244f-890e-46ef-be2f-a5202976b7a5/a/e2c43813-3c15-4468-ba9e-1743df42b19d?tenantId=24fe244f-890e-46ef-be2f-a5202976b7a5';
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];
const IMAGE_SIDE_SIZE = { width: 1200, height: 1200 };
const FILE_SIZE = 5242880;
export const MESSAGES = {
  bigFileSize: 'File size cannot be more then 5 Mb',
  importantNote: 'Important note: The cover image feature only displays thumbnail-sized images on WOL pages and social shares. All uploads here are auto-resized to thumbnails, so use it only for covers. For larger images, add them in the ‘VI DESCRIPTION’ input. To request this, raise a WOL support ticket ',
  bigImageSides: `Maximum image height ${IMAGE_SIDE_SIZE.height}px and width ${IMAGE_SIDE_SIZE.width}px`,
  moreThenOne: 'Choose one file please',
  fileFormat: 'Correct file format is .png or .jpg',
};

const useStyles = makeStyles((theme: ITheme) => ({
  'drop-block-wrapper': {
    width: '100%',
    height: '250px',

  },
  'drop-block': {
    width: '100%',
    height: '100%',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    color: theme.palette.secondary.main,
    transition: '0.2s',
    userSelect: 'none',
    '& h2': {
      marginBottom: theme.spacing(1),
    },
  },
  'drop-block_active': {
    color: theme.palette.typical.accent,
  },
  'drop-block__icon': {
    width: '2.5rem',
    height: '2.5rem',
  },
  'drop-block__info': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.grey['400'],
  },
  'image-block-wrapper': {
    display: 'flex',
    justifyContent: 'center',
    overflowY: 'auto',
  },
  'image-block': {
    display: 'inline-block',
    borderRadius: theme.shape.borderRadius,
    '& img': {
      display: 'block',
      border: `1px solid ${theme.palette.grey['300']}`,
      width: '20rem',
      borderRadius: '10px',
    },
  },
  'image-block__head': {
    textAlign: 'right',
    '& button': {
      color: theme.palette.error.light,
    },
  },
  fontColor: {
    color: theme.palette.secondary.main,
  },
  'cover-tab-wrapper': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: `1px solid ${theme.palette.grey['400']}`,
    borderRadius: '5px',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    position: 'relative',
    '&:hover': {
      borderColor: theme.palette.grey['800'],
      color: theme.palette.grey['800'],
    },
  },
  'cover-tab-title': {
    position: 'absolute',
    top: 0,
    left: 0,
    fontSize: '1rem',
    transform: 'translate(0px, -12px) scale(0.75)',
    backgroundColor: theme.palette.common.white,
    padding: '0px 6px',
    color: theme.palette.grey['600'],
  },
}));

const DropBlock = ({ isActive }: {isActive: boolean}) => {
  const styles = useStyles();
  return (
    <div className={`${styles['drop-block']} ${isActive ? styles['drop-block_active'] : ''}`}>
      <CloudUploadIcon className={`${styles['drop-block__icon']} ${styles.fontColor}`} />
      <h2 className={styles.fontColor}>Drop a file to upload</h2>
      <span className={styles.fontColor}>or click here to choose one</span>
      <div className={styles['drop-block__info']}>
        <span>
          Please upload .png or .jpg files 5 Mb max.
        </span>
        <span>
          {MESSAGES.importantNote}
          <a href={RAISE_SUPPORT_TICKET_URL} target="_blank" rel="noopener noreferrer">
            here.
          </a>
        </span>
      </div>
    </div>
  );
};

const ImageBlock = ({ file, onClickRemove }: { file: any, onClickRemove: () => void }) => {
  const styles = useStyles();
  const { id, preview } = file;
  const src = id ? getImage(id) : preview;

  return (
    <div className={styles['image-block-wrapper']}>
      <div className={styles['image-block']}>
        <div className={styles['image-block__head']}>
          <IconButton aria-label="remove cover" size="small" onClick={onClickRemove} data-seleniumid="remove-cover-btn">
            <CancelIcon />
          </IconButton>
        </div>
        <img src={src} alt="Cover preview" data-seleniumid="img-cover" />
      </div>
    </div>
  );
};

export type Cover = {
  id?: string | null,
  preview?:string,
  [key: string]: any;
}

export interface ICoverTab {
  tabId: TabsID,
  cover?: Cover;
  setCover: (id: TabsID, url?: Cover) => void;
}

const CoverTab: FC<ICoverTab> = ({ tabId, cover, setCover }) => {
  const [isZoneActive, setIsZoneActive] = useState(false);
  const styles = useStyles();

  const onDropHandler = (files: any[]) => {
    if (files.length < 1) {
      return;
    }
    if (files[0].size > FILE_SIZE) {
      toastr.warning('Incorrect file', MESSAGES.bigFileSize);
      return;
    }
    const blob = URL.createObjectURL(files[0]);
    const img = new Image();

    img.src = blob;
    img.onload = () => {
      if (img.height > IMAGE_SIDE_SIZE.height || img.width > IMAGE_SIDE_SIZE.width) {
        toastr.warning('Incorrect file', MESSAGES.bigImageSides);
        return;
      }
      setCover(tabId, Object.assign(files[0], { preview: blob }));
    };
  };

  return (
    <div className={styles['cover-tab-wrapper']}>
      <span className={styles['cover-tab-title']}>
        VI Cover
      </span>
      { cover
        ? (
          <ImageBlock file={cover} onClickRemove={() => setCover(tabId)} />
        )
        : (
          <Dropzone
            onDrop={onDropHandler}
            onDragEnter={() => setIsZoneActive(true)}
            onDragLeave={() => setIsZoneActive(false)}
            onDropRejected={(files) => {
              setIsZoneActive(false);
              if (files.length > 1) {
                toastr.warning('Incorrect file', MESSAGES.moreThenOne);
              }
              if (files.length === 1) {
                toastr.warning('Incorrect file', MESSAGES.fileFormat);
              }
            }}
            accept={ACCEPTED_FILE_TYPES}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className={styles['drop-block-wrapper']}>
                <input {...getInputProps()} data-seleniumid="cover-input" />
                <DropBlock isActive={isZoneActive} />
              </div>
            )}
          </Dropzone>
        )
      }
    </div>
  );
};

export default memo(CoverTab);
